import React, { useMemo, useState } from 'react';
import { Info } from 'lucide-react';
import { formatCurrencyRounded as sharedFormatCurrencyRounded, currencySymbol as sharedCurrencySymbol } from '../../utils/currency';
import './TonightPickup.css';

// Helpers ---------------------------------------------------------------

const todayIso = () => new Date().toISOString().slice(0, 10);

const normalizeIsoDate = (value) => {
  if (!value) return todayIso();
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, '0');
    const day = String(value.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
  if (typeof value === 'string') return value.slice(0, 10);
  return todayIso();
};

const shiftIsoDateByYears = (isoDate, years) => {
  const [year, month, day] = String(isoDate || '').slice(0, 10).split('-').map(Number);
  if (!year || !month || !day) return null;
  const targetYear = year + years;
  const targetMonthLastDay = new Date(targetYear, month, 0).getDate();
  const shifted = new Date(targetYear, month - 1, Math.min(day, targetMonthLastDay));
  const shiftedYear = shifted.getFullYear();
  const shiftedMonth = String(shifted.getMonth() + 1).padStart(2, '0');
  const shiftedDay = String(shifted.getDate()).padStart(2, '0');
  return `${shiftedYear}-${shiftedMonth}-${shiftedDay}`;
};

const rowIsoDate = (row) =>
  normalizeIsoDate(row?.effectiveDate || row?.businessDate || row?.date);

const makeSelectionSet = (selectedLocations = ['all']) => (
  !selectedLocations.includes('all') && selectedLocations.length > 0
    ? new Set(selectedLocations.map((id) => parseInt(id, 10)).filter(Number.isFinite))
    : null
);

const formatCurrency = (value, currency) =>
  sharedFormatCurrencyRounded(value, currency);

const formatCompactCurrency = (value, currency) => {
  const v = Number(value) || 0;
  const sym = sharedCurrencySymbol(currency);
  if (Math.abs(v) >= 1_000_000) return `${sym}${(v / 1_000_000).toFixed(2)}M`;
  if (Math.abs(v) >= 1_000) return `${sym}${(v / 1_000).toFixed(0)}K`;
  return formatCurrency(v, currency);
};

const formatInt = (value) =>
  Math.round(Number(value) || 0).toLocaleString();

const nullableNumber = (value) => {
  if (value === null || value === undefined) return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

const isPickupCurrentRow = (row) => row?.pickupDataCurrent === true || row?.pickupDataCurrent === 1;
const isPickupStaleRow = (row) => row?.pickupDataCurrent === false || row?.pickupDataCurrent === 0;
const isPickupUsableRow = (row) => isPickupCurrentRow(row) || (!isPickupCurrentRow(row) && !isPickupStaleRow(row));

const formatDateLabel = (iso) => {
  if (!iso) return 'an older file';
  const date = new Date(`${String(iso).slice(0, 10)}T12:00:00`);
  if (Number.isNaN(date.getTime())) return String(iso).slice(0, 10);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const otbRoomsForRow = (row) => {
  const otbRooms = Number(row?.otbRooms);
  if (Number.isFinite(otbRooms) && otbRooms !== 0) return otbRooms;
  return Number(row?.occupiedRooms) || 0;
};

const otbAvailableRoomsForRow = (row) => {
  const otbAvailableRooms = Number(row?.otbAvailableRooms);
  if (Number.isFinite(otbAvailableRooms) && otbAvailableRooms !== 0) return otbAvailableRooms;
  return Number(row?.availableRooms) || 0;
};

// Build the selected-night snapshot from forecastingData. Sums across all
// (or filtered) locations for the dashboard anchor date.
const buildTonightSnapshot = (byDate, selectedLocations, referenceDate) => {
  if (!Array.isArray(byDate) || byDate.length === 0) return null;
  const anchorDate = normalizeIsoDate(referenceDate);
  const sel = makeSelectionSet(selectedLocations);
  const todayRows = byDate.filter((r) => {
    const date = rowIsoDate(r);
    if (date !== anchorDate) return false;
    if (sel && !sel.has(r.locationId)) return false;
    return true;
  });
  if (todayRows.length === 0) return null;
  const sum = (k) => todayRows.reduce((acc, r) => acc + (Number(r?.[k]) || 0), 0);
  const explicitFreshness = todayRows.some((r) => isPickupCurrentRow(r) || isPickupStaleRow(r));
  const currentPickupRows = todayRows.filter(isPickupCurrentRow);
  const pickupRows = explicitFreshness ? currentPickupRows : todayRows;
  const nullableSum = (rows, k) => {
    const values = rows
      .map((r) => nullableNumber(r?.[k]))
      .filter((v) => v !== null);
    if (values.length === 0) return null;
    return values.reduce((acc, value) => acc + value, 0);
  };
  const forecastReportDates = todayRows
    .map((r) => (r?.latestForecastReportDate ? normalizeIsoDate(r.latestForecastReportDate) : null))
    .filter(Boolean);
  const latestForecastReportDate = forecastReportDates.length
    ? forecastReportDates.sort()[forecastReportDates.length - 1]
    : null;
  const pickupDataCurrent = explicitFreshness ? currentPickupRows.length > 0 : true;
  const staleDays = todayRows
    .map((r) => nullableNumber(r?.pickupDataStaleDays))
    .filter((v) => v !== null);
  const available = todayRows.reduce((acc, r) => acc + otbAvailableRoomsForRow(r), 0);
  const occupied = todayRows.reduce((acc, r) => acc + otbRoomsForRow(r), 0);
  const roomRev = sum('roomRevenue');
  const totalRev = sum('totalRevenue');
  const otbRevenue = sum('otbForecast') || roomRev || totalRev;
  return {
    available,
    occupied,
    otbRooms: occupied,
    otbRevenue,
    roomRevenue: roomRev,
    totalRevenue: totalRev,
    pickupRooms1Day: nullableSum(pickupRows, 'pickupRooms1Day'),
    pickupRevenue1Day: nullableSum(pickupRows, 'pickupRevenue1Day'),
    pickupRooms7Day: nullableSum(pickupRows, 'pickupRooms7Day'),
    pickupRevenue7Day: nullableSum(pickupRows, 'pickupRevenue7Day'),
    pickupRooms30Day: nullableSum(pickupRows, 'pickupRooms30Day'),
    pickupRevenue30Day: nullableSum(pickupRows, 'pickupRevenue30Day'),
    latestForecastReportDate,
    pickupDataCurrent,
    pickupDataStaleDays: staleDays.length ? Math.max(...staleDays) : null,
    occupancy: available > 0 ? (occupied / available) * 100 : 0,
    adr: occupied > 0 ? otbRevenue / occupied : 0,
  };
};

// Build the next-N-night pace series for the portfolio.
const buildPaceSeries = (
  byDate,
  selectedLocations,
  referenceDate,
  days = 30,
  comparisonMode = 'none',
  comparisonByDate = []
) => {
  if (!Array.isArray(byDate) || byDate.length === 0) return null;
  const anchorDate = normalizeIsoDate(referenceDate);
  const sel = makeSelectionSet(selectedLocations);

  const lastYearRoomsByDay = new Map();
  if (comparisonMode === 'lastYear' && Array.isArray(comparisonByDate)) {
    comparisonByDate.forEach((r) => {
      const date = rowIsoDate(r);
      const shiftedDate = shiftIsoDateByYears(date, 1);
      if (!shiftedDate || shiftedDate < anchorDate) return;
      if (sel && !sel.has(r.locationId)) return;
      const cur = lastYearRoomsByDay.get(shiftedDate) || 0;
      lastYearRoomsByDay.set(shiftedDate, cur + (Number(r?.occupiedRooms) || 0));
    });
  }

  const byDay = new Map();
  byDate.forEach((r) => {
    const date = rowIsoDate(r);
    if (!date || date < anchorDate) return;
    if (sel && !sel.has(r.locationId)) return;
    if (!byDay.has(date)) {
      byDay.set(date, {
        date,
        ty: 0,
        available: 0,
        revenue: 0,
        ly: null,
        budget: null,
        pickup: null,
        pickupRevenue: null,
      });
    }
    const bucket = byDay.get(date);
    bucket.ty += otbRoomsForRow(r);
    bucket.available += otbAvailableRoomsForRow(r);
    bucket.revenue += Number(r?.otbForecast) || Number(r?.roomRevenue) || 0;
    const budgetRooms = nullableNumber(r?.budgetOccupiedRooms);
    if (budgetRooms !== null) {
      bucket.budget = bucket.budget === null ? budgetRooms : bucket.budget + budgetRooms;
    }
    if (isPickupUsableRow(r)) {
      const pickupRooms = nullableNumber(r?.pickupRooms1Day);
      const pickupRevenue = nullableNumber(r?.pickupRevenue1Day);
      if (pickupRooms !== null) {
        bucket.pickup = bucket.pickup === null ? pickupRooms : bucket.pickup + pickupRooms;
      }
      if (pickupRevenue !== null) {
        bucket.pickupRevenue = bucket.pickupRevenue === null ? pickupRevenue : bucket.pickupRevenue + pickupRevenue;
      }
    }
  });
  const arr = [...byDay.values()]
    .sort((a, b) => a.date.localeCompare(b.date))
    .slice(0, days)
    .map((bucket) => ({
      ...bucket,
      ly: lastYearRoomsByDay.has(bucket.date) ? lastYearRoomsByDay.get(bucket.date) : null,
    }));
  if (arr.length < 2) return null;
  return arr;
};

// Friday=5, Saturday=6 indices in JS Date.getDay() — flag weekends.
const isWeekendIso = (iso) => {
  const d = new Date(iso + 'T12:00:00');
  const dow = d.getDay();
  return dow === 5 || dow === 6;
};

// Tile rendering ---------------------------------------------------------

function Tile({
  label,
  value,
  sub,
  delta,
  note,
  accent = false,
  stub = false,
}) {
  return (
    <div className={'tp-tile' + (accent ? ' accent' : '')}>
      <div className="tp-tile-label">{label}</div>
      <div className={'tp-tile-value' + (value === '—' ? ' muted' : '')}>{value}</div>
      {sub && <div className="tp-tile-sub">{sub}</div>}
      <div className="tp-tile-foot">
        {delta && (
          <span className={'tp-tile-delta ' + delta.dir}>
            {delta.dir === 'up' ? '+' : '−'}{delta.pct}%
          </span>
        )}
        {note && <span className="tp-tile-note">{note}</span>}
        {stub && <span className="tp-tile-stub">Pickup history coming soon</span>}
      </div>
    </div>
  );
}

// Pace chart -------------------------------------------------------------

function PaceChart({ pace, capacity, comparisonMode, currency }) {
  const [hover, setHover] = useState(null);
  const normalizedComparison = comparisonMode || 'none';

  if (!pace || pace.length < 2) {
    return (
      <div className="tp-chart-card">
        <div className="tp-chart-head">
          <div>
            <div className="tp-chart-title">Portfolio OTB pace · next 30 nights</div>
            <div className="tp-chart-sub">Rooms on the books per night, all properties combined</div>
          </div>
        </div>
        <div style={{
          padding: '32px 0',
          textAlign: 'center',
          color: 'var(--lodge-onyx-500)',
          fontSize: 13,
          fontFamily: 'var(--font-sans)',
        }}>
          Not enough forecast data yet to draw the next-30-night pace curve.
        </div>
      </div>
    );
  }

  // Chart geometry
  const W = 1180;
  const H = 220;
  const PADX = 44;
  const PADT = 18;
  const PADB = 32;
  const innerW = W - PADX * 2;
  const innerH = H - PADT - PADB;

  const hasLy = normalizedComparison === 'lastYear' && pace.some((p) => p.ly != null);
  const hasBudget = normalizedComparison === 'budget' && pace.some((p) => p.budget != null);
  const hasPickup = normalizedComparison === 'pickup' && pace.some((p) => p.pickup != null);
  const comparisonValues = [
    ...(hasLy ? pace.map((p) => Number(p.ly) || 0) : []),
    ...(hasBudget ? pace.map((p) => Number(p.budget) || 0) : []),
  ];
  const yMax = Math.max(capacity || 0, ...pace.map((p) => p.ty), ...comparisonValues) || 1;
  const yMin = 0;
  const xAt = (i) => PADX + (i / (pace.length - 1)) * innerW;
  const yAt = (v) => PADT + (1 - (v - yMin) / (yMax - yMin)) * innerH;

  const tyPath = pace.map((p, i) => `${i ? 'L' : 'M'}${xAt(i)},${yAt(p.ty)}`).join(' ');
  const tyArea = `${tyPath} L${xAt(pace.length - 1)},${yAt(yMin)} L${xAt(0)},${yAt(yMin)} Z`;
  const lyPath = hasLy
    ? pace.map((p, i) => (p.ly != null ? `${i ? 'L' : 'M'}${xAt(i)},${yAt(p.ly)}` : null)).filter(Boolean).join(' ')
    : null;
  const budgetPath = hasBudget
    ? pace.map((p, i) => (p.budget != null ? `${i ? 'L' : 'M'}${xAt(i)},${yAt(p.budget)}` : null)).filter(Boolean).join(' ')
    : null;
  const pickupMax = hasPickup
    ? Math.max(1, ...pace.map((p) => Math.abs(Number(p.pickup) || 0)))
    : 1;
  const pickupBarMax = Math.min(52, innerH * 0.32);
  const pickupZeroY = PADT + innerH - 8;
  const pickupBarW = Math.max(3, Math.min(14, innerW / pace.length * 0.45));

  const chartTitle = hasPickup
    ? `Portfolio OTB pace & pickup · next ${pace.length} nights`
    : `Portfolio OTB pace · next ${pace.length} nights`;
  const chartSubtitle = (() => {
    if (hasPickup) return 'Rooms on the books with overnight pickup by forward stay date';
    if (hasBudget) return 'Rooms on the books per night vs forward room budget';
    if (hasLy) return 'Rooms on the books per night vs last year final rooms';
    return 'Rooms on the books per night, all properties combined';
  })();

  // Horizontal grid: capacity, 80%, 60%, 40%, 20%
  const grid = [
    { v: capacity, label: 'CAP', strong: true },
    { v: capacity * 0.8, label: '80%' },
    { v: capacity * 0.6, label: '60%' },
    { v: capacity * 0.4, label: '40%' },
    { v: capacity * 0.2, label: '20%' },
  ].filter((g) => Number.isFinite(g.v) && g.v > 0);

  // X-axis label stops
  const stops = [0, 7, 14, 21, pace.length - 1].filter((v, i, a) => a.indexOf(v) === i && v < pace.length);
  const labelFor = (i) => {
    if (i === 0) return 'Tonight';
    return `+${i}d`;
  };

  return (
    <div className="tp-chart-card">
      <div className="tp-chart-head">
        <div>
          <div className="tp-chart-title">{chartTitle}</div>
          <div className="tp-chart-sub">{chartSubtitle}</div>
        </div>
        <div className="tp-chart-legend">
          <span className="tp-leg ty"><span className="tp-leg-sw" />This year · OTB</span>
          {hasLy && (
            <span className="tp-leg ly"><span className="tp-leg-sw" />Last year · final RNs</span>
          )}
          {hasBudget && (
            <span className="tp-leg budget"><span className="tp-leg-sw" />Budget rooms</span>
          )}
          {hasPickup && (
            <span className="tp-leg pickup"><span className="tp-leg-sw" />Overnight pickup</span>
          )}
          {capacity > 0 && (
            <span className="tp-leg cap"><span className="tp-leg-sw" />Capacity {Math.round(capacity).toLocaleString()}</span>
          )}
        </div>
      </div>

      <div className="tp-chart-wrap" onMouseLeave={() => setHover(null)}>
        <svg className="tp-chart" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none">
          <defs>
            <linearGradient id="tpArea" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="var(--lodge-orange-400)" stopOpacity="0.22" />
              <stop offset="100%" stopColor="var(--lodge-orange-400)" stopOpacity="0" />
            </linearGradient>
          </defs>

          {/* Weekend shading */}
          {pace.map((p, i) => isWeekendIso(p.date) && (
            <rect key={'w' + i}
              x={xAt(i) - innerW / (pace.length - 1) / 2}
              y={PADT}
              width={innerW / (pace.length - 1)}
              height={innerH}
              fill="var(--lodge-onyx-50)" />
          ))}

          {/* Y grid */}
          {grid.map((g, i) => (
            <g key={'g' + i}>
              <line x1={PADX} x2={W - PADX}
                y1={yAt(g.v)} y2={yAt(g.v)}
                stroke={g.strong ? 'var(--lodge-onyx-300)' : 'var(--lodge-onyx-100)'}
                strokeWidth={1}
                strokeDasharray={g.strong ? '' : '2 4'} />
              <text x={PADX - 8} y={yAt(g.v) + 3}
                textAnchor="end"
                fontSize="10"
                fontFamily="var(--font-mono)"
                fill={g.strong ? 'var(--lodge-onyx-700)' : 'var(--lodge-onyx-400)'}
                fontWeight={g.strong ? 700 : 500}>
                {g.label}
              </text>
            </g>
          ))}

          {/* TY area + line */}
          <path d={tyArea} fill="url(#tpArea)" />
          {lyPath && (
            <path d={lyPath} fill="none" stroke="var(--lodge-onyx-400)"
              strokeWidth="1.5" strokeDasharray="3 3" strokeLinecap="round" />
          )}
          {budgetPath && (
            <path d={budgetPath} fill="none" stroke="var(--lodge-onyx-500)"
              strokeWidth="1.5" strokeDasharray="5 4" strokeLinecap="round" />
          )}
          <path d={tyPath} fill="none" stroke="var(--lodge-orange-400)"
            strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round" />

          {hasPickup && pace.map((p, i) => {
            if (p.pickup == null) return null;
            const pickup = Number(p.pickup) || 0;
            const h = Math.abs(pickup) / pickupMax * pickupBarMax;
            const y = pickup >= 0 ? pickupZeroY - h : pickupZeroY;
            return (
              <rect key={'pu' + i}
                x={xAt(i) - pickupBarW / 2}
                y={y}
                width={pickupBarW}
                height={Math.max(2, h)}
                rx="2"
                fill={pickup >= 0 ? 'var(--color-success, #12B76A)' : 'var(--color-error, #F04438)'}
                opacity="0.72" />
            );
          })}

          {/* Tonight marker */}
          <line x1={xAt(0)} x2={xAt(0)} y1={PADT} y2={PADT + innerH}
            stroke="var(--lodge-onyx-700)" strokeWidth="1" strokeDasharray="2 3" />
          <circle cx={xAt(0)} cy={yAt(pace[0].ty)} r="4.5"
            fill="var(--lodge-orange-400)" stroke="#fff" strokeWidth="2" />

          {/* X labels */}
          {stops.map((s) => (
            <text key={s}
              x={xAt(s)} y={H - 10}
              textAnchor={s === 0 ? 'start' : s === pace.length - 1 ? 'end' : 'middle'}
              fontSize="10"
              fontFamily="var(--font-mono)"
              fill={s === 0 ? 'var(--lodge-orange-500)' : 'var(--lodge-onyx-500)'}
              fontWeight={s === 0 ? 700 : 500}>
              {labelFor(s)}
            </text>
          ))}

          {/* Hover hit areas */}
          {pace.map((p, i) => (
            <rect key={'h' + i}
              x={xAt(i) - innerW / (pace.length - 1) / 2}
              y={PADT}
              width={innerW / (pace.length - 1)}
              height={innerH}
              fill="transparent"
              onMouseEnter={() => setHover(i)} />
          ))}

          {/* Hover indicators */}
          {hover != null && (
            <g pointerEvents="none">
              <line x1={xAt(hover)} x2={xAt(hover)} y1={PADT} y2={PADT + innerH}
                stroke="var(--lodge-onyx-700)" strokeWidth="1" opacity="0.4" />
              <circle cx={xAt(hover)} cy={yAt(pace[hover].ty)} r="4"
                fill="var(--lodge-orange-400)" stroke="#fff" strokeWidth="2" />
              {pace[hover].ly != null && (
                <circle cx={xAt(hover)} cy={yAt(pace[hover].ly)} r="3.5"
                  fill="var(--lodge-onyx-400)" stroke="#fff" strokeWidth="1.5" />
              )}
              {hasBudget && pace[hover].budget != null && (
                <circle cx={xAt(hover)} cy={yAt(pace[hover].budget)} r="3.5"
                  fill="var(--lodge-onyx-500)" stroke="#fff" strokeWidth="1.5" />
              )}
            </g>
          )}
        </svg>

        {hover != null && (
          <div className="tp-tooltip"
            style={{
              left: `${(xAt(hover) / W) * 100}%`,
              transform: hover > pace.length - 6
                ? 'translate(-100%, -100%)' : 'translate(0, -100%)',
            }}>
            <div className="tp-tooltip-head">
              <span>
                {hover === 0 ? 'Tonight' : `Tonight + ${hover}d`}
                <span className="tp-tooltip-date">
                  {formatDateLabel(pace[hover].date)}
                </span>
              </span>
              {isWeekendIso(pace[hover].date) && <span className="tp-tooltip-wknd">Weekend</span>}
            </div>
            <div className="tp-tooltip-rows">
              <div>
                <span className="tp-tt-sw tp-tt-ty" />
                <span className="tp-tt-lbl">This yr OTB</span>
                <span className="tp-tt-val">
                  {Math.round(pace[hover].ty).toLocaleString()}
                  {pace[hover].available > 0 && (
                    <span className="tp-tt-occ"> · {((pace[hover].ty / pace[hover].available) * 100).toFixed(0)}%</span>
                  )}
                </span>
              </div>
              {pace[hover].ly != null && (
                <>
                  <div>
                    <span className="tp-tt-sw tp-tt-ly" />
                    <span className="tp-tt-lbl">Last yr final</span>
                    <span className="tp-tt-val">{Math.round(pace[hover].ly).toLocaleString()}</span>
                  </div>
                  <div className="tp-tt-divider" />
                  <div>
                    <span className="tp-tt-lbl">Pace vs LY</span>
                    <span className={'tp-tt-val ' + (pace[hover].ty >= pace[hover].ly ? 'pos' : 'neg')}>
                      {pace[hover].ty >= pace[hover].ly ? '+' : '−'}
                      {Math.abs(Math.round(pace[hover].ty - pace[hover].ly)).toLocaleString()} rooms
                    </span>
                  </div>
                </>
              )}
              {hasBudget && pace[hover].budget != null && (
                <>
                  <div>
                    <span className="tp-tt-sw tp-tt-budget" />
                    <span className="tp-tt-lbl">Budget rooms</span>
                    <span className="tp-tt-val">{Math.round(pace[hover].budget).toLocaleString()}</span>
                  </div>
                  <div className="tp-tt-divider" />
                  <div>
                    <span className="tp-tt-lbl">Pace vs budget</span>
                    <span className={'tp-tt-val ' + (pace[hover].ty >= pace[hover].budget ? 'pos' : 'neg')}>
                      {pace[hover].ty >= pace[hover].budget ? '+' : '−'}
                      {Math.abs(Math.round(pace[hover].ty - pace[hover].budget)).toLocaleString()} rooms
                    </span>
                  </div>
                </>
              )}
              {hasPickup && pace[hover].pickup != null && (
                <>
                  <div className="tp-tt-divider" />
                  <div>
                    <span className="tp-tt-sw tp-tt-pickup" />
                    <span className="tp-tt-lbl">Overnight pickup</span>
                    <span className={'tp-tt-val ' + (pace[hover].pickup >= 0 ? 'pos' : 'neg')}>
                      {pace[hover].pickup >= 0 ? '+' : '−'}
                      {Math.abs(Math.round(pace[hover].pickup)).toLocaleString()} rooms
                    </span>
                  </div>
                  {pace[hover].pickupRevenue != null && (
                    <div>
                      <span className="tp-tt-lbl">Revenue pickup</span>
                      <span className={'tp-tt-val ' + (pace[hover].pickupRevenue >= 0 ? 'pos' : 'neg')}>
                        {formatCurrency(pace[hover].pickupRevenue, currency)}
                      </span>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="tp-chart-foot">
        <span>
          <Info size={11} color="var(--lodge-onyx-400)" />
          Hover any night to inspect OTB · Weekend nights shaded
        </span>
      </div>
    </div>
  );
}

// Public component -------------------------------------------------------

function TonightPickup({
  forecastingData,
  comparisonData,
  dashboardData,
  forecastingMetrics,
  calculatedMetrics,
  selectedLocations = ['all'],
  lastUpdated,
  referenceDate,
  forecastWindow,
  forecastComparison,
  currency,
  children,
}) {
  const tonight = useMemo(
    () => buildTonightSnapshot(forecastingData?.byDate, selectedLocations, referenceDate),
    [forecastingData, selectedLocations, referenceDate]
  );

  const paceNights = forecastWindow?.nights || 30;
  const pace = useMemo(
    () => buildPaceSeries(
      forecastingData?.byDate,
      selectedLocations,
      referenceDate,
      paceNights,
      forecastComparison,
      comparisonData?.byDate
    ),
    [forecastingData, selectedLocations, referenceDate, paceNights, forecastComparison, comparisonData]
  );

  const portfolioCapacity = useMemo(() => {
    if (tonight?.available) return tonight.available;
    if (!Array.isArray(forecastingData?.byDate)) return 0;
    const anchorDate = normalizeIsoDate(referenceDate);
    const sel = makeSelectionSet(selectedLocations);
    const byLoc = new Map();
    forecastingData.byDate.forEach((r) => {
      const date = rowIsoDate(r);
      if (!date || date < anchorDate) return;
      if (sel && !sel.has(r.locationId)) return;
      const cur = byLoc.get(r.locationId) || 0;
      byLoc.set(r.locationId, Math.max(cur, Number(r?.availableRooms) || 0));
    });
    let total = 0;
    byLoc.forEach((v) => { total += v; });
    return total;
  }, [forecastingData, selectedLocations, tonight, referenceDate]);

  const occText = tonight ? `${tonight.occupancy.toFixed(1)}%` : '—';
  const occSub = tonight
    ? `${formatInt(tonight.occupied)} / ${formatInt(tonight.available)} rooms`
    : '—';
  const otbText = tonight ? formatInt(tonight.otbRooms) : '—';
  const otbSub = tonight
    ? `${formatCurrency(tonight.otbRevenue, currency)} forecast revenue`
    : '—';
  const adrText = tonight && tonight.adr > 0 ? `ADR ${formatCurrency(tonight.adr, currency)}` : null;

  const buildPickupTile = (days, roomsKey, revenueKey) => {
    if (!tonight) {
      return {
        value: '—',
        sub: `Rooms added in the last ${days === 1 ? '24h' : `${days} days`}`,
      };
    }

    const rooms = nullableNumber(tonight[roomsKey]);
    const revenue = nullableNumber(tonight[revenueKey]);
    if (!tonight.pickupDataCurrent || rooms === null || revenue === null) {
      return {
        value: '—',
        sub: `Latest forecast file ${formatDateLabel(tonight.latestForecastReportDate)}`,
        note: "Pickup waits for today's file",
      };
    }

    const roomPrefix = rooms > 0 ? '+' : rooms < 0 ? '−' : '';
    return {
      value: `${roomPrefix}${formatInt(Math.abs(rooms))}`,
      sub: `${formatCurrency(revenue, currency)} revenue pickup`,
    };
  };

  const oneDayPickup = buildPickupTile(1, 'pickupRooms1Day', 'pickupRevenue1Day');
  const sevenDayPickup = buildPickupTile(7, 'pickupRooms7Day', 'pickupRevenue7Day');
  const thirtyDayPickup = buildPickupTile(30, 'pickupRooms30Day', 'pickupRevenue30Day');

  return (
    <div className="tp-section">
      {/* No section header — the Forecast tab above already names this, and
          the period/comparison it described are set in the filter bar. */}

      {/* Joined sheet: tiles, pace chart and whatever the tab nests below
          (the pickup table) share hairlines instead of floating apart. */}
      <div className="rn-sheet">
      <div className="tp-tiles rn-joinrow">
        <Tile
          label="Tonight's Occupancy"
          value={occText}
          sub={occSub}
          accent
        />
        <Tile
          label="On the Books · Tonight"
          value={otbText}
          sub={otbSub}
          note={adrText}
        />
        <Tile
          label="Tonight 1-Day Pickup"
          value={oneDayPickup.value}
          sub={oneDayPickup.sub}
          note={oneDayPickup.note}
        />
        <Tile
          label="Tonight 7-Day Pickup"
          value={sevenDayPickup.value}
          sub={sevenDayPickup.sub}
          note={sevenDayPickup.note}
        />
        <Tile
          label="Tonight 30-Day Pickup"
          value={thirtyDayPickup.value}
          sub={thirtyDayPickup.sub}
          note={thirtyDayPickup.note}
        />
      </div>

      <PaceChart pace={pace} capacity={portfolioCapacity} comparisonMode={forecastComparison} currency={currency} />
      {children}
      </div>
    </div>
  );
}

export default TonightPickup;
export { TonightPickup };
